import Close from "@mui/icons-material/Close";
import OpenInFull from "@mui/icons-material/OpenInFull";
import Remove from "@mui/icons-material/Remove";
import {
  Box,
  Chip,
  IconButton,
  Popover,
  type PopoverOrigin,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ScoutIcon from "components/ScoutIcon";
import { useTenantId } from "hooks/useTenant";
import { useThemeGreyBackgroundColor } from "hooks/useThemeGreyBackgroundColor";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { ChatChrome } from "../Chat/ChatChrome";
import { ScoutChatDropzone } from "../Chat/ScoutChatDropzone";
import { ScoutInput, type ScoutSubmitHandler } from "../Chat/ScoutInput";
import { useScoutAllowMutations } from "../hooks/useScoutAllowMutations";
import { useScoutConversation } from "../hooks/useScoutConversation";
import { SCOUT_ACCESS_PACKAGES, useScoutAccessPackage } from "../scoutAccess";
import { ScoutConversationSettingsMenu } from "../ScoutConversationSettingsMenu";
import { type ScoutPopoverSession } from "./useScoutPopoverSession";

/**
 * Pins the popover to a viewport corner with a fixed pixel margin. Used by
 * the sidebar footer trigger so the popover and its minimized form share the
 * same on-screen position. When omitted the popover anchors to `anchorEl`.
 */
export type PopoverScoutDock = {
  corner: "bottom-left" | "bottom-right";
  offsetX: number;
  offsetY: number;
};

export type PopoverScoutProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Shared session state (conversationId, openedAt) created by the caller. */
  session: ScoutPopoverSession;
  /**
   * When provided, a minimize button is shown in the header. Clicking it
   * should drop the popover into a docked/minimized state. The caller is
   * responsible for what "minimized" looks like — this just signals intent.
   */
  onMinimize?: () => void;
  /**
   * Free-form extra context to append after the auto-built page context (URL,
   * title, open time). Useful for "Explain {term}"-style triggers where the
   * caller knows the specific thing the user is asking about. The popover
   * always includes its own page-aware context — this prop only enriches it.
   */
  extraContext?: string;
  /** MUI Popover anchorOrigin (defaults to bottom-right of the trigger). */
  anchorOrigin?: PopoverOrigin;
  /** MUI Popover transformOrigin (defaults to top-right of the popover). */
  transformOrigin?: PopoverOrigin;
  /**
   * Dock the popover to a viewport corner instead of anchoring to `anchorEl`.
   * When set, `anchorEl`/`anchorOrigin`/`transformOrigin` are ignored.
   */
  dock?: PopoverScoutDock;
};

const POPOVER_WIDTH = 800;
const POPOVER_HEIGHT = 600;
const DEFAULT_ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "right",
};
const DEFAULT_TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: "top",
  horizontal: "right",
};

const CONTEXT_CHIP_TOOLTIP =
  "When you open Scout this way, it always knows where you are in the application and can answer questions about anything you're currently looking at.";

function buildPopoverPageContext(input: {
  pathname: string;
  search: string;
  pageTitle: string;
  openedAt: Date;
  extra?: string;
}): string {
  const url = `${input.pathname}${input.search}`;
  const lines = [
    `Current page URL: ${url}`,
    `Current page title: ${input.pageTitle}`,
    `Popover conversation started at: ${input.openedAt.toISOString()}`,
  ];
  if (input.extra) {
    lines.push(input.extra);
  }
  return lines.join("\n");
}

/**
 * In-context Scout conversation rendered as an MUI Popover. Reuses the same
 * controller and store as the main Scout nav, so conversations started here
 * show up in the user's Scout thread list. Session state (conversationId,
 * openedAt) is held by the caller via `useScoutPopoverSession` so the popover
 * can be unmounted (e.g. minimized to a docked bar) without losing the active
 * thread. Each turn carries an auto-built page-context snapshot (URL + title
 * + open time) into the system prompt.
 */
export function PopoverScout({
  anchorEl,
  open,
  onClose,
  onMinimize,
  session,
  extraContext,
  anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
  transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
  dock,
}: PopoverScoutProps) {
  const theme = useTheme();
  const canvasColor = useThemeGreyBackgroundColor();
  // `useTenantId()` prefers the URL path param (`/o/:tenantId/...` for
  // customer routes, `/staff/implementation/:tenantId` for staff) and falls
  // back to the session-storage `savedTenant`. This is what we want for staff
  // pages with no `:tenantId` in the URL — picking `user.UserTenant[0]` is
  // wrong for staff because staff users have no `UserTenant` rows. The
  // sidebar trigger hides the launcher when this is undefined, so this
  // branch is effectively the staff-with-saved-tenant case.
  const tenantId = useTenantId();
  const { scoutAccessPackage } = useScoutAccessPackage();
  const hasScoutProAccess = scoutAccessPackage === SCOUT_ACCESS_PACKAGES.PRO;
  const { pathname, search } = useLocation();
  const { conversationId, setConversationId, openedAt } = session;

  // When docked, recompute the anchor point so the popover stays pinned to
  // the corner if the user resizes the window while it's open. Element-mode
  // (anchorEl) is unaffected — MUI handles that path internally.
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === "undefined" ? 0 : window.innerWidth,
    height: typeof window === "undefined" ? 0 : window.innerHeight,
  }));
  useEffect(() => {
    if (!dock) {
      return;
    }
    const onResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [dock]);

  const dockAnchorPosition = useMemo(() => {
    if (!dock) {
      return undefined;
    }
    // Pin the popover's chosen corner at the offsets the caller asked for,
    // measured in from that corner of the viewport.
    const top = viewport.height - dock.offsetY;
    const left = dock.corner === "bottom-right" ? viewport.width - dock.offsetX : dock.offsetX;
    return { top, left };
  }, [dock, viewport.height, viewport.width]);
  const dockTransformOrigin = useMemo<PopoverOrigin | undefined>(() => {
    if (!dock) {
      return undefined;
    }
    return {
      vertical: "bottom",
      horizontal: dock.corner === "bottom-right" ? "right" : "left",
    };
  }, [dock]);

  const {
    conversation,
    messages,
    activeTurn,
    streamStatus,
    loading,
    isSending,
    isAborting,
    isLoadingOlderMessages,
    hasOlderMessages,
    sendMessage,
    abortTurn,
    loadOlderMessages,
  } = useScoutConversation({
    tenantId,
    conversationId,
    onConversationIdChange: setConversationId,
    enablePrivateDocuments: hasScoutProAccess,
  });

  // Write-access toggle. Mirrors the pattern in routes/Customer/Scout/Chat:
  // local state takes precedence after the user touches it, otherwise we sync
  // from the server-resolved conversation so a resumed thread reflects the
  // value it was last sent with.
  const { allowMutations, onAllowMutationsChange } = useScoutAllowMutations({
    conversationId,
    conversationAllowMutations: conversation?.allowMutations,
    resetWhenConversationClears: true,
  });

  const isTurnRunning =
    isSending ||
    isAborting ||
    activeTurn?.status === "starting" ||
    activeTurn?.status === "streaming";
  const hasMessages = messages.length > 0;
  const isLoadingConversation = Boolean(conversationId) && loading && !hasMessages;
  const showIntro = !hasMessages && !isLoadingConversation;

  const handleSubmit = useCallback<ScoutSubmitHandler>(
    async (message, options) => {
      const pageContext = buildPopoverPageContext({
        pathname,
        search,
        pageTitle: typeof document !== "undefined" ? document.title : "",
        openedAt,
        extra: extraContext,
      });
      const attachments = options?.attachments;
      await sendMessage(message, {
        pageContext,
        allowMutations: hasScoutProAccess ? allowMutations : false,
        attachmentIds: hasScoutProAccess
          ? attachments?.map((attachment) => attachment.privateDocumentId)
          : undefined,
        attachmentPreviews: hasScoutProAccess
          ? attachments?.map((attachment) => ({
              privateDocumentId: attachment.privateDocumentId,
              filename: attachment.filename,
              mediaType: attachment.mediaType,
              sizeBytes: attachment.sizeBytes,
              signedDownloadUrl: attachment.signedDownloadUrl,
              thumbnailSignedUrl: attachment.thumbnailSignedUrl,
              pageCount: attachment.pageCount,
            }))
          : undefined,
        extractionWatchPrivateDocumentIds: options?.extractionWatchPrivateDocumentIds,
      });
    },
    [allowMutations, extraContext, hasScoutProAccess, openedAt, pathname, search, sendMessage]
  );

  const handleAbort = useCallback(() => {
    void abortTurn();
  }, [abortTurn]);

  const fullPageHref = useMemo(() => {
    if (!tenantId) {
      return undefined;
    }
    return conversationId ? `/o/${tenantId}/scout/${conversationId}` : `/o/${tenantId}/scout`;
  }, [tenantId, conversationId]);

  return (
    <Popover
      open={open}
      onClose={onClose}
      // MUI Popover wraps Paper in a Modal whose root spans the viewport and
      // locks body scroll, blocking every interaction with the page behind it.
      // For an in-context chat we want the user to keep scrolling, hovering,
      // and clicking the underlying page while the popover stays open — so:
      //   1. `disableScrollLock` lets the body keep scrolling.
      //   2. `hideBackdrop` removes the invisible backdrop that would have
      //      otherwise eaten clicks even with pointer-events disabled.
      //   3. `pointerEvents: "none"` on the modal root lets mouse/scroll
      //      events fall through to the page; we re-enable on the Paper so
      //      the popover itself stays fully interactive.
      // Trade-off: clicking outside the popover no longer triggers `onClose`
      // (the Modal's outside-click handler can't fire when its root ignores
      // pointer events). The minimize button, the X, and ESC remain — and
      // chat-widget patterns (Front, Intercom) already work this way.
      disableScrollLock
      hideBackdrop
      slotProps={{
        root: {
          sx: { pointerEvents: "none" },
        },
        paper: {
          sx: {
            width: POPOVER_WIDTH,
            height: POPOVER_HEIGHT,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            pointerEvents: "auto",
            // One uniform canvas color across header, body and context footer
            // so the popover reads as a single surface instead of a stack of
            // panels.
            bgcolor: canvasColor,
            // Per-spec popover margins: 16px above the header, 40px below the
            // chat input. Horizontal 24px is applied per-section so the chat
            // area can compensate for ChatChrome's own footer padding.
            pt: 2,
            pb: 5,
          },
        },
      }}
      {...(dock && dockAnchorPosition && dockTransformOrigin
        ? {
            anchorReference: "anchorPosition" as const,
            anchorPosition: dockAnchorPosition,
            transformOrigin: dockTransformOrigin,
          }
        : {
            anchorEl,
            anchorOrigin,
            transformOrigin,
          })}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 3, mb: 3 }}>
        <Box
          component={fullPageHref ? RouterLink : "div"}
          {...(fullPageHref ? { to: fullPageHref, onClick: onClose } : {})}
          aria-label={fullPageHref ? "Open Scout in full view" : undefined}
          sx={{
            // Hug the header content so the clickable area matches what's
            // visible rather than stretching across the empty flex space
            // between the logo and the action buttons.
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            color: "inherit",
            textDecoration: "none",
            cursor: fullPageHref ? "pointer" : "default",
          }}
        >
          <Typography variant="overline" sx={{ letterSpacing: 1, lineHeight: 1 }}>
            SCOUT
          </Typography>
          <ScoutIcon sx={{ fontSize: 20, color: theme.palette.encampBlue.main }} />
        </Box>
        <Box sx={{ flex: 1 }} />
        {onMinimize ? (
          <Tooltip title="Minimize">
            <IconButton size="small" onClick={onMinimize} aria-label="Minimize Scout popover">
              <Remove fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <ScoutConversationSettingsMenu
          allowMutations={hasScoutProAccess ? allowMutations : false}
          onAllowMutationsChange={hasScoutProAccess ? onAllowMutationsChange : () => undefined}
          disabled={isTurnRunning || !hasScoutProAccess}
          iconButtonSize="small"
          hasScoutProAccess={hasScoutProAccess}
        />
        <Tooltip title="Open in full Scout">
          <span>
            <IconButton
              component={RouterLink}
              to={fullPageHref ?? "#"}
              onClick={onClose}
              size="small"
              aria-label="Open in full Scout"
              disabled={!fullPageHref}
            >
              <OpenInFull fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} aria-label="Close Scout popover">
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      {/*
        ChatChrome's footer (the input) has p:2 (16px) internal padding. The
        extra 8px here gives a clean 24px gap from the popover paper edge to
        the chat input edge without needing to modify the shared ChatChrome.
      */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          px: 1,
        }}
      >
        {showIntro ? (
          <PopoverIntro
            tenantId={tenantId}
            conversationId={conversationId}
            onSubmit={handleSubmit}
            onAbort={handleAbort}
            isSending={isTurnRunning}
            isAborting={isAborting}
            allowMutations={hasScoutProAccess ? allowMutations : false}
            onAllowMutationsChange={hasScoutProAccess ? onAllowMutationsChange : undefined}
            hasScoutProAccess={hasScoutProAccess}
          />
        ) : (
          <ChatChrome
            tenantId={tenantId}
            conversationId={conversationId}
            messages={messages}
            activeTurn={activeTurn}
            streamStatus={streamStatus}
            loading={isLoadingConversation}
            initialPinned
            onSubmit={handleSubmit}
            onAbort={handleAbort}
            isSending={isTurnRunning}
            isAborting={isAborting}
            onLoadOlderMessages={loadOlderMessages}
            isLoadingOlderMessages={isLoadingOlderMessages}
            hasOlderMessages={hasOlderMessages}
            allowMutations={hasScoutProAccess ? allowMutations : false}
            onAllowMutationsChange={hasScoutProAccess ? onAllowMutationsChange : undefined}
            hasScoutProAccess={hasScoutProAccess}
          />
        )}
      </Box>
      {/*
        Negative top margin pulls the Context section up into ChatChrome's
        footer padding so there's less dead space below the "Scout is AI…"
        disclaimer before the Context subheading. ChatChrome's footer is
        absolutely positioned with zIndex 2, so we need a positioned context
        with a higher zIndex (and an opaque background) here, otherwise the
        footer covers the top of the "Context" heading.
      */}
      <Box
        sx={{
          px: 3,
          mt: -1.5,
          position: "relative",
          zIndex: 3,
          bgcolor: canvasColor,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ display: "block", mb: 0 }}>
          Context
        </Typography>
        <Tooltip title={CONTEXT_CHIP_TOOLTIP} placement="top-start">
          <Chip
            size="small"
            label="Knows this page"
            variant="outlined"
            sx={{ cursor: "default", bgcolor: theme.palette.action.selected }}
          />
        </Tooltip>
      </Box>
    </Popover>
  );
}

/**
 * Empty-state intro view shown before the first message. Mirrors the main
 * Scout page's NewChat layout (centered icon + greeting + input) but omits
 * the suggested-query chips so the popover stays in-context.
 */
function PopoverIntro({
  tenantId,
  conversationId,
  onSubmit,
  onAbort,
  isSending,
  isAborting,
  allowMutations,
  onAllowMutationsChange,
  hasScoutProAccess,
}: {
  tenantId?: string;
  conversationId?: string;
  onSubmit: ScoutSubmitHandler;
  onAbort: () => void;
  isSending: boolean;
  isAborting: boolean;
  allowMutations: boolean;
  onAllowMutationsChange?: (next: boolean) => void;
  hasScoutProAccess: boolean;
}) {
  const theme = useTheme();

  return (
    <ScoutChatDropzone
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          px: 2,
          py: 3,
        }}
      >
        <ScoutIcon sx={{ color: theme.palette.encampBlue.main, width: 56, height: 56 }} />
        <Typography variant="subtitle1" textAlign="center">
          Hey there! I&apos;m Scout, your Encamp guide. How can I help you today?
        </Typography>
        <Box sx={{ width: "100%" }}>
          <ScoutInput
            tenantId={tenantId}
            conversationId={conversationId}
            onSubmit={onSubmit}
            onAbort={onAbort}
            isSending={isSending}
            isAborting={isAborting}
            placeholder="Ask Scout a question about this page…"
            allowMutations={allowMutations}
            onAllowMutationsChange={onAllowMutationsChange}
            hasScoutProAccess={hasScoutProAccess}
          />
        </Box>
      </Stack>
    </ScoutChatDropzone>
  );
}
